import React from 'react'
import { useTranslation } from 'react-i18next'
import { Pencil, Trash2 } from 'lucide-react'
import { useConfirm } from '../../../contexts/ConfirmDialogContext'

function macrosLine(meal, t) {
  const p = meal.protein_g
  const c = meal.carbs_g
  const f = meal.fat_g
  if (p == null && c == null && f == null) return null
  return t('phoenixTracker.meals.macrosShort', {
    protein: p != null ? Number(p).toFixed(p % 1 === 0 ? 0 : 1) : '—',
    carbs: c != null ? Number(c).toFixed(c % 1 === 0 ? 0 : 1) : '—',
    fat: f != null ? Number(f).toFixed(f % 1 === 0 ? 0 : 1) : '—',
  })
}

// Composite meals saved by the builder use " + " as a separator between
// item names (e.g. "Whole egg + Whole wheat toast + Apple"). Render each
// piece as its own chip so long combinations don't get truncated.
function splitItems(name) {
  if (!name) return []
  return name
    .split(' + ')
    .map((s) => s.trim())
    .filter(Boolean)
}

export function MealCard({ meal, onEdit, onDelete }) {
  const { t } = useTranslation()
  const confirm = useConfirm()
  const macros = macrosLine(meal, t)
  const items = splitItems(meal.name)

  const handleDelete = async () => {
    const ok = await confirm({
      title: t('phoenixTracker.meals.confirmDelete'),
      description: t('phoenixTracker.meals.confirmDeleteDesc'),
      confirmLabel: t('phoenixTracker.meals.delete'),
      variant: 'destructive',
      icon: Trash2,
    })
    if (ok) await onDelete(meal.id)
  }

  return (
    <div className="rounded-md bg-muted/40 p-3">
      {/* Item chips. dir="auto" so each chip resolves its own direction
          (Arabic items render RTL, English items LTR) and they wrap onto
          multiple lines instead of truncating. */}
      <div className="flex flex-wrap gap-1.5">
        {items.map((item, i) => (
          <span
            key={`${item}-${i}`}
            dir="auto"
            className="inline-flex items-center px-2 py-0.5 rounded-full bg-background border border-input text-[11px] text-foreground max-w-full truncate"
          >
            {item}
          </span>
        ))}
      </div>

      {/* Macros + actions row. dir="auto" lets the paragraph pick its base
          direction from the first strong character (kcalUnit + macrosShort
          contain Arabic letters in AR), and each numeric+unit chunk is
          isolated in <bdi> so it stays cohesive. */}
      <div className="flex items-center justify-between gap-2 mt-2">
        <p className="text-xs text-muted-foreground font-mono min-w-0 truncate" dir="auto">
          <bdi className="text-app-primary-500 font-semibold text-sm">{meal.kcal}</bdi>
          <span className="ms-1">{t('phoenixTracker.meals.kcalUnit')}</span>
          {macros && (
            <>
              <span className="mx-1.5">·</span>
              <bdi>{macros}</bdi>
            </>
          )}
        </p>
        <div className="flex items-center gap-0.5 flex-shrink-0">
          <button
            type="button"
            onClick={() => onEdit(meal)}
            className="p-1.5 rounded-md hover:bg-accent text-muted-foreground hover:text-foreground"
            aria-label={t('phoenixTracker.meals.form.editTitle')}
          >
            <Pencil className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            onClick={handleDelete}
            className="p-1.5 rounded-md hover:bg-red-100 dark:hover:bg-red-900/30 text-muted-foreground hover:text-red-500"
            aria-label={t('phoenixTracker.meals.delete')}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {meal.notes && (
        <p className="text-xs text-muted-foreground italic mt-1.5 whitespace-pre-wrap" dir="auto">
          {meal.notes}
        </p>
      )}
    </div>
  )
}
